import { Box, Button, Flex, IconButton, Input, Text, VStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

const GREEN = "#089A7A";

function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
}

/**
 * Props:
 * - available: liczba dostępnych miejsc (np. 20)
 * - maxPerOrder?: maks na zamówienie (jeśli brak, przyjmie `available`)
 * - value: liczba wybranych biletów (controlled)
 * - onChange: (next:number) => void
 * - onReserve?: () => void  (klik „Zarezerwuj”)
 * - label?: string (np. "Wybierz ilość biletów")
 * - isLoading?: boolean (blokuje kontrolki)
 */
const FreeTicketsQuantity = ({
    available,
    maxPerOrder,
    value,
    onChange,
    onReserve,
    label,
    isLoading = false,
}) => {
    const { t } = useTranslation("default");
    const displayLabel = label || t('ticket.choose_quantity');
    const limit = Number.isFinite(maxPerOrder) ? Math.min(available ?? 0, maxPerOrder) : (available ?? 0);
    const canInc = value < limit && !isLoading;
    const canDec = value > 0 && !isLoading;
    const disabledReserve = value === 0 || isLoading;

    const inc = useCallback(() => {
        if (!canInc) return;
        onChange(clamp(value + 1, 0, limit));
    }, [canInc, value, limit, onChange]);

    const dec = useCallback(() => {
        if (!canDec) return;
        onChange(clamp(value - 1, 0, limit));
    }, [canDec, value, limit, onChange]);

    const onType = (e) => {
        const raw = e.target.value.replace(/[^\d]/g, "");
        const n = raw === "" ? 0 : parseInt(raw, 10);
        onChange(clamp(isNaN(n) ? 0 : n, 0, limit));
    };

    const onEnter = (e) => {
        if (e.key === "Enter" && !disabledReserve && onReserve) onReserve();
    };

    return (
        <VStack w="full" spacing={4} align="stretch" gap="4px">
            <Text textAlign="center" color="#616161" fontFamily={'Nunito'} fontWeight={'500'} fontSize={'18px'}>{displayLabel}</Text>

            <Flex align="center" justify="center" gap="12px" w="full">
                <IconButton
                    aria-label={t('action.decrease')}
                    onClick={dec}
                    isDisabled={!canDec}
                    bgColor={GREEN}
                    color="white"
                    _disabled={{ bg: "#D7E6E2", cursor: "not-allowed" }}
                    _hover={{ bg: GREEN }}
                    borderRadius="4px"
                    w="49px"
                    h="49px"
                    icon={<Box as="span" fontSize="24px" lineHeight="1">−</Box>}
                    flex="1"
                />

                <Input
                    value={value}
                    onChange={onType}
                    onKeyDown={onEnter}
                    textAlign="center"
                    fontWeight={600}
                    border="1.5px solid #E0E0E0"
                    borderRadius="4px"
                    w="full"
                    h="49px"
                    bg="white"
                    _focus={{ borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    flex="4"
                />

                <IconButton
                    aria-label={t('action.increase')}
                    onClick={inc}
                    isDisabled={!canInc}
                    bgColor={GREEN}
                    color="white"
                    _disabled={{ bg: "#D7E6E2", cursor: "not-allowed" }}
                    _hover={{ bg: GREEN }}
                    borderRadius="4px"
                    w="49px"
                    h="49px"
                    icon={<Box as="span" fontSize="24px" lineHeight="1">+</Box>}
                    flex="1"
                />
            </Flex>

            <Button
                onClick={onReserve}
                isDisabled={disabledReserve || !onReserve}
                isLoading={isLoading}
                bgColor={GREEN}
                color="white"
                _hover={{ bg: GREEN }}
                _disabled={{ bg: "#B6B6B6", color: "#9E9E9E" }}
                borderRadius={"4px"}
                h="49px"
                mt="16px"
                leftIcon={
                    <svg width="17" height="21" viewBox="0 0 17 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.5 3.5H3.5C2.96957 3.5 2.46086 3.71071 2.08579 4.08579C1.71071 4.46086 1.5 4.96957 1.5 5.5V17.5C1.5 18.0304 1.71071 18.5391 2.08579 18.9142C2.46086 19.2893 2.96957 19.5 3.5 19.5H13.5C14.0304 19.5 14.5391 19.2893 14.9142 18.9142C15.2893 18.5391 15.5 18.0304 15.5 17.5V5.5C15.5 4.96957 15.2893 4.46086 14.9142 4.08579C14.5391 3.71071 14.0304 3.5 13.5 3.5H11.5M5.5 3.5C5.5 4.03043 5.71071 4.53914 6.08579 4.91421C6.46086 5.28929 6.96957 5.5 7.5 5.5H9.5C10.0304 5.5 10.5391 5.28929 10.9142 4.91421C11.2893 4.53914 11.5 4.03043 11.5 3.5M5.5 3.5C5.5 2.96957 5.71071 2.46086 6.08579 2.08579C6.46086 1.71071 6.96957 1.5 7.5 1.5H9.5C10.0304 1.5 10.5391 1.71071 10.9142 2.08579C11.2893 2.46086 11.5 2.96957 11.5 3.5M8.5 10.5H11.5M8.5 14.5H11.5M5.5 10.5H5.51M5.5 14.5H5.51" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                }
            >
                <Text fontFamily={'Nunito'} fontWeight={'400'} fontSize={'18px'} color={"white"}>
                    {t('ticket.reserve')}
                </Text>
            </Button>
        </VStack>
    );
};

export default FreeTicketsQuantity;
